import { useState, useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { LogOutIcon } from '@/components/icons';
import { useOpenClawConnection } from '@/hooks/use-openclaw-connection';

const font = { fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" };

export default function UserProfileDropdown({ onClose }) {
  const ref = useRef(null);
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState('Guest');
  // Canonical gateway-connection source — same hook the rest of the app uses,
  // so this menu stays in sync with the dashboard's connected/seed logic.
  // `minimal: true` → only hits /me, skips the heavier /dashboard call since
  // the menu doesn't need the full agents list.
  const { ready: connectionReady, connected: isConnected } =
    useOpenClawConnection({ minimal: true });

  // Load user identity in background — show instantly with defaults
  useEffect(() => {
    (async () => {
      try {
        const supabase = createClient();
        const { data: { user: authUser } } = await supabase.auth.getUser();
        setUser(authUser);
        setUsername(authUser?.email?.split('@')[0] || 'Guest');
      } catch {}
    })();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const handleSignOut = async () => {
    const supabase = createClient();
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  const initial = username?.[0]?.toUpperCase() || '?';

  const itemStyle = {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '5px 8px', borderRadius: 8,
    ...font, fontSize: 10, color: 'rgba(255,255,255,0.4)',
    cursor: 'pointer', background: 'none', border: 'none',
    width: '100%', textAlign: 'left',
    transition: 'all 0.15s',
  };

  return (
    <div ref={ref} style={{
      width: 200, background: 'rgba(6,6,7,0.85)',
      backdropFilter: 'blur(24px)',
      WebkitBackdropFilter: 'blur(24px)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 16, padding: 6,
      ...font, fontSize: 10,
      boxShadow: '0px 8px 32px rgba(0,0,0,0.3)',
      animation: 'fadeIn 0.12s ease-out',
      display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      {/* User info block */}
      <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: 12, padding: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px' }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 600, color: 'white', flexShrink: 0,
          }}>
            {initial}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'white', fontWeight: 500, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {username}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.email || ''}
            </div>
          </div>
        </div>
      </div>

      {/* Actions block */}
      <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: 12, padding: 6 }}>
        {/* OpenClaw status */}
        <div style={{ ...itemStyle, justifyContent: 'space-between', cursor: 'default' }}>
          <span>OpenClaw</span>
          {!connectionReady ? (
            <span style={{
              fontSize: 8, padding: '1px 6px', borderRadius: 8,
              background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
                style={{ animation: 'spin 0.9s linear infinite' }}>
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              </svg>
              Checking
            </span>
          ) : isConnected ? (
            <span style={{
              fontSize: 8, padding: '1px 6px', borderRadius: 8,
              background: 'rgba(50,213,131,0.15)', color: '#32d583',
            }}>
              Connected
            </span>
          ) : (
            <a href="/onboarding" style={{
              fontSize: 8, padding: '1px 6px', borderRadius: 8,
              background: 'rgba(251,191,36,0.15)', color: '#fbbf24',
              textDecoration: 'none',
            }}>
              Connect
            </a>
          )}
        </div>

        {/* Sign out */}
        <button onClick={handleSignOut} style={itemStyle}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#f87171'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
        >
          <LogOutIcon size={11} /> Sign out
        </button>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
